import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/redux/store";
import { analyticsAPI } from "@/lib/services/api.service";

interface CallStats {
  total_calls: number;
  completed_calls: number;
  missed_calls: number;
  average_duration: number;
  total_duration: number;
  meetings_booked: number;
  conversion_rate: number;
  callsByDate?: Record<string, number>;
  meetingsByDate?: Record<string, number>;
}

interface VisitorStats {
  total_visitors: number;
  active_visitors: number;
  total_page_views: number;
  average_time_on_site: number;
  bounce_rate: number;
  visitorsByDate?: Record<string, number>;
}

interface ConversionStats {
  calls_to_meetings: number;
  visitor_to_calls: number;
  total_conversions: number;
}

interface TimeSeriesData {
  date: string;
  calls: number;
  visitors: number;
  meetings: number;
}

interface AnalyticsState {
  callStats: CallStats | null;
  visitorStats: VisitorStats | null;
  conversionStats: ConversionStats | null;
  timeSeriesData: TimeSeriesData[];
  salesRepPerformance: any[];
  isLoading: boolean;
  error: string | null;
  dateRange: {
    start: string;
    end: string;
  };
}

const today = new Date();
const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

const initialState: AnalyticsState = {
  callStats: null,
  visitorStats: null,
  conversionStats: null,
  timeSeriesData: [],
  salesRepPerformance: [],
  isLoading: false,
  error: null,
  dateRange: {
    start: weekAgo.toISOString(),
    end: today.toISOString(),
  },
};

export const fetchAnalyticsData = createAsyncThunk(
  "analytics/fetchData",
  async ({ startDate, endDate }: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const [callStatsRes, visitorStatsRes, conversionStatsRes, performanceRes] = await Promise.all(
        [
          analyticsAPI.getCallStats(startDate, endDate),
          analyticsAPI.getVisitorStats(startDate, endDate),
          analyticsAPI.getConversionStats(startDate, endDate),
          analyticsAPI.getSalesRepPerformance(),
        ]
      );

      // Normalize legacy `{ stats: ... }` shape and newer direct payload shape
      const callStatsData: any = (callStatsRes as any)?.stats ?? callStatsRes ?? {};
      const visitorStatsData: any = (visitorStatsRes as any)?.stats ?? visitorStatsRes ?? {};
      const conversionStatsData: any =
        (conversionStatsRes as any)?.stats ?? conversionStatsRes ?? {};
      const performanceData: any =
        (performanceRes as any)?.performance ?? (performanceRes as any)?.stats?.performance ?? [];

      // Backend returns camelCase fields; map to the snake_case structure the UI expects
      const mappedCallStats: CallStats = {
        total_calls: callStatsData.totalCalls ?? 0,
        completed_calls: callStatsData.completedCalls ?? 0,
        missed_calls:
          callStatsData.missedCalls ??
          ((callStatsData.totalCalls ?? 0) - (callStatsData.completedCalls ?? 0) || 0),
        average_duration: callStatsData.averageDuration ?? 0,
        total_duration: callStatsData.totalDuration ?? 0,
        meetings_booked: callStatsData.meetingsBooked ?? 0,
        conversion_rate: callStatsData.conversionRate ?? 0,
        callsByDate: callStatsData.callsByDate || {},
        meetingsByDate: callStatsData.meetingsByDate || {},
      };

      const mappedVisitorStats: VisitorStats = {
        total_visitors: visitorStatsData.totalVisitors ?? 0,
        active_visitors: visitorStatsData.activeCount ?? 0,
        total_page_views:
          visitorStatsData.total_page_views ??
          visitorStatsData.totalPageViews ??
          visitorStatsData.uniqueVisitors ??
          visitorStatsData.totalVisitors ??
          0,
        average_time_on_site:
          visitorStatsData.average_time_on_site ?? visitorStatsData.averageTimeOnSite ?? 0,
        bounce_rate: visitorStatsData.bounce_rate ?? visitorStatsData.bounceRate ?? 0,
        visitorsByDate: visitorStatsData.visitorsByDate || {},
      };

      const mappedConversionStats: ConversionStats = {
        calls_to_meetings:
          conversionStatsData.conversionRates?.callToMeeting ??
          conversionStatsData.call_to_meetings ??
          0,
        visitor_to_calls:
          conversionStatsData.conversionRates?.visitorToCall ??
          conversionStatsData.visitor_to_calls ??
          0,
        total_conversions:
          conversionStatsData.conversionRates?.overall ??
          conversionStatsData.total_conversions ??
          0,
      };

      return {
        callStats: mappedCallStats,
        visitorStats: mappedVisitorStats,
        conversionStats: mappedConversionStats,
        performance: performanceData || [],
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch analytics data");
    }
  }
);

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    setCallStats: (state, action: PayloadAction<CallStats>) => {
      state.callStats = action.payload;
    },
    setVisitorStats: (state, action: PayloadAction<VisitorStats>) => {
      state.visitorStats = action.payload;
    },
    setConversionStats: (state, action: PayloadAction<ConversionStats>) => {
      state.conversionStats = action.payload;
    },
    setTimeSeriesData: (state, action: PayloadAction<TimeSeriesData[]>) => {
      state.timeSeriesData = action.payload;
    },
    setDateRange: (state, action: PayloadAction<{ start: string; end: string }>) => {
      state.dateRange = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnalyticsData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.callStats = action.payload.callStats;
        state.visitorStats = action.payload.visitorStats;
        state.conversionStats = action.payload.conversionStats;
        state.salesRepPerformance = action.payload.performance;

        // Process time series data from stats
        // This is a simplified mapping, ideally backend should return combined time series
        const callsByDate = action.payload.callStats.callsByDate || {};
        const visitorsByDate = action.payload.visitorStats.visitorsByDate || {};
        const meetingsByDate = action.payload.callStats.meetingsByDate || {};

        const allDates = new Set([
          ...Object.keys(callsByDate),
          ...Object.keys(visitorsByDate),
          ...Object.keys(meetingsByDate),
        ]);

        state.timeSeriesData = Array.from(allDates)
          .sort()
          .map((date) => ({
            date,
            calls: callsByDate[date] || 0,
            visitors: visitorsByDate[date] || 0,
            meetings: meetingsByDate[date] || 0,
          }));
      })
      .addCase(fetchAnalyticsData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCallStats,
  setVisitorStats,
  setConversionStats,
  setTimeSeriesData,
  setDateRange,
  setLoading,
  setError,
} = analyticsSlice.actions;

// Provide a typed selector to avoid `unknown` on state.analytics
export const selectAnalyticsState = (state: RootState): AnalyticsState =>
  state.analytics as AnalyticsState;

export const selectCallStats = (state: RootState) => selectAnalyticsState(state).callStats;
export const selectVisitorStats = (state: RootState) => selectAnalyticsState(state).visitorStats;
export const selectConversionStats = (state: RootState) =>
  selectAnalyticsState(state).conversionStats;
export const selectTimeSeriesData = (state: RootState) =>
  selectAnalyticsState(state).timeSeriesData;
export const selectSalesRepPerformance = (state: RootState) =>
  selectAnalyticsState(state).salesRepPerformance;
export const selectDateRange = (state: RootState) => selectAnalyticsState(state).dateRange;
export const selectAnalyticsLoading = (state: RootState) => selectAnalyticsState(state).isLoading;

export default analyticsSlice.reducer;
